<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { scoreFilterRange } from '$lib/stores/selection';

  let minValue = 0;
  let maxValue = 100;
  let sliderElement: HTMLElement | undefined;
  let isDraggingMin = false;
  let isDraggingMax = false;

  /**
   * How strongly a thumb is tinted, by the score it sits at.
   *
   * One hue, five steps. It was five different hues — red, amber, green,
   * orange, emerald — which is a rainbow rather than a scale: nothing about
   * orange says "lower than green", so the encoding had to be learned instead
   * of read. A single ramp is ordinal on sight.
   */
  function getThumbColor(value: number) {
    if (value >= 80) return 'border-accent-700';
    if (value >= 65) return 'border-accent-600';
    if (value >= 45) return 'border-accent-500';
    if (value >= 30) return 'border-accent-400';
    return 'border-accent-300';
  }

  // Subscribe to the store
  $: {
    [minValue, maxValue] = $scoreFilterRange;
  }

  // Update the store when values change
  function updateRange() {
    scoreFilterRange.set([minValue, maxValue]);
  }

  // Handle slider drag events
  function handleMouseMove(e: MouseEvent) {
    if (!isDraggingMin && !isDraggingMax) return;

    if (!sliderElement) return;

    const rect = sliderElement.getBoundingClientRect();
    const percent = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const value = Math.round(percent);

    if (isDraggingMin && value <= maxValue) {
      minValue = value;
      updateRange();
    } else if (isDraggingMax && value >= minValue) {
      maxValue = value;
      updateRange();
    }
  }

  function handleMouseUp() {
    isDraggingMin = false;
    isDraggingMax = false;
  }

  // Handle input changes
  function handleMinInput(e: Event & { currentTarget: HTMLInputElement }) {
    const value = parseInt((e.currentTarget as HTMLInputElement).value) || 0;
    if (value >= 0 && value <= maxValue) {
      minValue = value;
      updateRange();
    }
  }

  function handleMaxInput(e: Event & { currentTarget: HTMLInputElement }) {
    const value = parseInt((e.currentTarget as HTMLInputElement).value) || 100;
    if (value >= minValue && value <= 100) {
      maxValue = value;
      updateRange();
    }
  }
</script>

<svelte:window on:mousemove={handleMouseMove} on:mouseup={handleMouseUp} />

<div class="flex w-full flex-col gap-1 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
  <span class="whitespace-nowrap text-xs font-medium text-gray-600"
    >{$_('sorting.matchScoreLabel')}:</span
  >

  <div class="flex items-center gap-2">
    <!-- Min input -->
    <input
      type="number"
      min="0"
      max="100"
      value={minValue}
      on:input={handleMinInput}
      class="w-12 rounded-control border border-gray-300 px-2 py-1 text-center text-xs [appearance:textfield] focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />

    <!-- Range slider container -->
    <div
      class="relative flex h-5 flex-1 items-center sm:w-32 sm:flex-none"
      bind:this={sliderElement}
    >
      <!-- The track is the range not chosen, so it is neutral. -->
      <div class="absolute h-1 w-full rounded-full bg-gray-200"></div>

      <!--
        The chosen range is a selection, so it is the accent.

        The background-position and background-size arithmetic went with the
        gradient: it existed to keep a five-stop rainbow aligned to the track
        while the range moved. A flat fill needs none of it.
      -->
      <div
        class="absolute h-1 rounded-full bg-accent-500"
        style="left: {minValue}%; right: {100 - maxValue}%;"
      ></div>

      <!-- Min thumb -->
      <div
        class="absolute h-3 w-3 cursor-pointer rounded-full border-2 bg-white transition-colors {isDraggingMin
          ? 'scale-125'
          : ''} {getThumbColor(minValue)}"
        style="left: {minValue}%; transform: translateX(-50%);"
        on:mousedown|preventDefault={() => (isDraggingMin = true)}
        role="slider"
        aria-label={$_('sorting.matchScoreLabel')}
        aria-valuenow={minValue}
        aria-valuemin="0"
        aria-valuemax="100"
        tabindex="0"
      ></div>

      <!-- Max thumb -->
      <div
        class="absolute h-3 w-3 cursor-pointer rounded-full border-2 bg-white transition-colors {isDraggingMax
          ? 'scale-125'
          : ''} {getThumbColor(maxValue)}"
        style="left: {maxValue}%; transform: translateX(-50%);"
        on:mousedown|preventDefault={() => (isDraggingMax = true)}
        role="slider"
        aria-label={$_('sorting.matchScoreLabel')}
        aria-valuenow={maxValue}
        aria-valuemin="0"
        aria-valuemax="100"
        tabindex="0"
      ></div>
    </div>

    <!-- Max input -->
    <input
      type="number"
      min="0"
      max="100"
      value={maxValue}
      on:input={handleMaxInput}
      class="w-12 rounded-control border border-gray-300 px-2 py-1 text-center text-xs [appearance:textfield] focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
    />
  </div>
</div>
